package org.dronda.app.web.ui.events

import androidx.compose.runtime.Stable
import com.github.michaelbull.result.onErr
import com.github.michaelbull.result.onOk
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import org.dronda.app.web.repository.EventRepository
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import org.dronda.service.wishlist.core.domain.EventMode
import org.dronda.service.wishlist.core.domain.WishlistEvent
import org.dronda.app.web.ui.util.formatMedium
import kotlin.time.Clock

class EventsViewModel(
    private val eventRepository: EventRepository
) {
    private val scope = CoroutineScope(Dispatchers.Default + SupervisorJob())
    private val _uiState = MutableStateFlow<UiState>(UiState.Loading)
    val uiState: StateFlow<UiState> = _uiState.asStateFlow()

    init {
        loadEvents()
    }

    fun take(event: ViewEvent) {
        when (event) {
            ViewEvent.Refresh -> loadEvents()
        }
    }

    private fun loadEvents() {
        scope.launch {
            _uiState.value = UiState.Loading
            eventRepository.getEvents()
                .onOk { events ->
                    val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
                    val (past, upcoming) = events.partition { it.date < today }
                    _uiState.value = UiState.Success(
                        upcoming = upcoming.map { it.toCardDisplay() },
                        past = past.sortedByDescending { it.date }.map { it.toCardDisplay() },
                    )
                }
                .onErr {
                    _uiState.value = UiState.Error("Failed to load events")
                }
        }
    }

    private fun WishlistEvent.toCardDisplay() = EventCardDisplay(
        id = id.toString(),
        name = name,
        date = date.formatMedium(),
        badge = if (mode == EventMode.SECRET_SANTA) "🎅 Secret Santa" else null,
    )

    data class EventCardDisplay(
        val id: String,
        val name: String,
        val date: String,
        val badge: String?,
    )

    @Stable
    sealed interface UiState {
        data object Loading : UiState
        data class Success(
            val upcoming: List<EventCardDisplay>,
            val past: List<EventCardDisplay>,
        ) : UiState
        data class Error(val message: String) : UiState
    }

    sealed interface ViewEvent {
        data object Refresh : ViewEvent
    }
}
